import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select,
  MenuItem, Box, Alert, CircularProgress, Typography, Stack
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { existeNewsletterDelMes, subirNewsletter } from "../../http/actions/NewsletterAction";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const SubirNewsletterModal = ({ open, onClose, tituloInicial, anioInicial, mesInicial, onSubido }) => {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [archivo, setArchivo] = useState(null);

  const [verificando, setVerificando] = useState(false);
  const [yaExiste, setYaExiste] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setTitulo(tituloInicial ?? "");
      setDescripcion("");
      setAnio(anioInicial ?? new Date().getFullYear());
      setMes(mesInicial ?? new Date().getMonth() + 1);
      setArchivo(null);
      setError("");
      setYaExiste(false);
    }
  }, [open, tituloInicial, anioInicial, mesInicial]);

  useEffect(() => {
    if (!open || !anio || !mes) return;
    setVerificando(true);
    const token = localStorage.getItem("token");
    existeNewsletterDelMes(token, anio, mes)
      .then((res) => setYaExiste(res.existe))
      .catch(() => setYaExiste(false))
      .finally(() => setVerificando(false));
  }, [open, anio, mes]);

  const handleArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError("El archivo debe ser un PDF.");
      setArchivo(null);
      return;
    }
    setError("");
    setArchivo(file);
  };

  const handleSubir = () => {
    if (!titulo.trim()) return setError("Ingresa un título.");
    if (!archivo) return setError("Adjunta el PDF del newsletter.");
    if (yaExiste) return setError("Ya existe un newsletter para el mes seleccionado.");

    setSubiendo(true);
    setError("");
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("archivo", archivo);
    formData.append("Titulo", titulo.trim());
    formData.append("Descripcion", descripcion.trim());
    formData.append("Anio", anio);
    formData.append("Mes", mes);

    subirNewsletter(token, formData)
      .then((res) => {
        onSubido?.(res);
        onClose();
      })
      .catch((err) => {
        const msg = err?.response?.data?.mensaje ?? "Error al subir el newsletter.";
        setError(msg);
      })
      .finally(() => setSubiendo(false));
  };

  const anios = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  return (
    <Dialog open={open} onClose={subiendo ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Publicar newsletter</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {yaExiste && !error && (
            <Alert severity="warning">
              Ya existe un newsletter registrado para {MESES[mes - 1]} de {anio}.
            </Alert>
          )}

          <TextField
            label="Título"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Descripción general"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
          />

          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Mes</InputLabel>
              <Select value={mes} label="Mes" onChange={(e) => setMes(e.target.value)}>
                {MESES.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Año</InputLabel>
              <Select value={anio} label="Año" onChange={(e) => setAnio(e.target.value)}>
                {anios.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={verificando}
            >
              {archivo ? "Cambiar PDF" : "Adjuntar PDF"}
              <input type="file" accept="application/pdf" hidden onChange={handleArchivo} />
            </Button>
            {archivo && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {archivo.name} ({(archivo.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={subiendo}>Cancelar</Button>
        <Button
            variant="contained"
            onClick={handleSubir}
            disabled={subiendo || verificando || yaExiste || !archivo}
            startIcon={subiendo ? <CircularProgress size={18} color="inherit" /> : null}
        >
        {subiendo ? "Publicando..." : "Publicar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubirNewsletterModal;